import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { color, typography } from '@uniquegood/realworld-studio-design';
import PageHeader from './components/PageHeader';
import ContactForm from './components/ContactForm';
import PrivacyAgreement from './components/PrivacyAgreement';
import SubmitSuccess from './components/SubmitSuccess';
import { useContactForm } from './hooks/useContactForm';
import usePageExit from '@/hooks/usePageExit';
import { PageViewEventName, track } from '@/track';
import { getAccessToken } from '@/auth';

const useScrollAnimation = (triggerOnMount = false) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // 마운트 시 바로 애니메이션 트리거
    if (triggerOnMount) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }

    // 스크롤 시 애니메이션 트리거
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [triggerOnMount]);

  return { ref, isVisible };
};

const PricingContact = () => {
  const { control, register, handleSubmit, errors, isValid, isSubmitted, watch } = useContactForm();

  const headerAnimation = useScrollAnimation(true);
  const formAnimation = useScrollAnimation(true);
  const privacyAnimation = useScrollAnimation(true);
  const buttonAnimation = useScrollAnimation(true);

  const formValues = watch();

  // 폼이 변경되었는지 확인
  const isFormDirty = Object.values(formValues).some((value) => {
    if (typeof value === 'string') return value.trim() !== '';
    if (typeof value === 'boolean') return value;
    return false;
  });

  usePageExit(
    () => {
      // 페이지 이탈 시 실행될 콜백
    },
    {
      enabled: isFormDirty && !isSubmitted,
      shouldBlockRouting: true,
      shouldPreventDefault: true // 브라우저 기본 confirm 사용
    }
  );

  const viewEvent = async () => {
    const accessToken = await getAccessToken();
    track.onPageView({
      pageViewEventName: PageViewEventName.view_plan_enterprise_form,
      params: { is_logged_in: accessToken ? 'true' : 'false' }
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    viewEvent();
  }, []);

  if (isSubmitted) {
    return <SubmitSuccess />;
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <StyledForm onSubmit={handleSubmit}>
          <HeaderSection ref={headerAnimation.ref} isVisible={headerAnimation.isVisible}>
            <PageHeader
              title="엔터프라이즈 요금제 맞춤 견적"
              subtitle="기업과 조직을 위한 맞춤 플랜을 합리적인 가격으로 제안해드립니다."
            />
          </HeaderSection>

          <FormSection ref={formAnimation.ref} isVisible={formAnimation.isVisible}>
            <ContactForm control={control} register={register} errors={errors} />
          </FormSection>

          <PrivacySection ref={privacyAnimation.ref} isVisible={privacyAnimation.isVisible}>
            <PrivacyAgreement register={register} error={errors?.personalInfoConsent?.message} />
          </PrivacySection>

          <ButtonSection ref={buttonAnimation.ref} isVisible={buttonAnimation.isVisible}>
            <SubmitButton type="submit" disabled={!isValid}>
              맞춤 상담 신청하기
            </SubmitButton>
          </ButtonSection>
        </StyledForm>
      </ContentWrapper>
    </PageContainer>
  );
};

export default PricingContact;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  min-width: 100%;
  background: #f5f5f5;
  align-items: center;
`;

const ContentWrapper = styled.div`
  max-width: 640px;
  width: 100%;
  padding: 80px 24px 64px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const HeaderSection = styled.div<{ isVisible: boolean }>`
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '30px')});
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
`;

const FormSection = styled.div<{ isVisible: boolean }>`
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '30px')});
  transition: opacity 0.6s ease-out 0.1s, transform 0.6s ease-out 0.1s;
`;

const PrivacySection = styled.div<{ isVisible: boolean }>`
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '30px')});
  transition: opacity 0.6s ease-out 0.2s, transform 0.6s ease-out 0.2s;
`;

const ButtonSection = styled.div<{ isVisible: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '30px')});
  transition: opacity 0.6s ease-out 0.3s, transform 0.6s ease-out 0.3s;
`;

const SubmitButton = styled.button<{ disabled: boolean }>`
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.medium};
  background: ${({ disabled }) => (disabled ? '#e8e8e8' : '#7800ff')};
  color: ${({ disabled }) => (disabled ? '#adadad' : color.white)};
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: opacity 0.2s;

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.9)};
  }
`;
